import type {State} from 'src/reducers';
import type {
  PhoneNumberSet,
  GenericOptionsType,
} from 'src/types/chat/phone-number-sets';

import {createSelector} from 'reselect';

import {values} from 'src/utils/object';
import {first} from 'src/utils/array';
import {getCountryFromPhone, formatPhone} from 'src/utils/phone';
import {resolveDisplayPhoneNumber} from 'src/utils/phone-number-sets';

import {selectDefaultPhoneCountry} from 'src/selectors/agency';


export const selectPhoneNumberSetsArray: (arg1: State) => PhoneNumberSet[] =
  createSelector(
    (state: State) => state.chat.phoneNumberSets,
    (phoneNumberSets): PhoneNumberSet[] => values(phoneNumberSets),
  );

export const selectAllAssignedPhoneNumberSetIds: (arg1: State) => string[] =
  createSelector(selectPhoneNumberSetsArray, (phoneNumberSets): string[] => {
    if (!phoneNumberSets) {
      return [];
    }
    return phoneNumberSets.reduce<Array<any>>(
      (acc: string[], set: PhoneNumberSet) => {
        if (set.sms?.id) {
          acc.push(set.sms.id);
        }
        if (set.whatsapp?.id) {
          acc.push(set.whatsapp.id);
        }
        return acc;
      },
      [],
    );
  });

export const selectPhoneNumberSetFromId = (phoneNumberSetId: string) =>
  createSelector(selectPhoneNumberSetsArray, (phoneNumberSetsArray) =>
    phoneNumberSetsArray.find(
      (phoneNumberSet) =>
        phoneNumberSet.sms?.id === phoneNumberSetId ||
        phoneNumberSet.whatsapp?.id === phoneNumberSetId,
    ),
  );

export const selectPhoneFromPhoneNumberSetId = (phoneNumberSetId: string) =>
  createSelector(
    selectPhoneNumberSetFromId(phoneNumberSetId),
    (phoneNumberSet) => phoneNumberSet?.phone_numbers?.[0],
  );

export const selectOrderedPhoneNumberSetsArray: (
  arg1: State,
) => PhoneNumberSet[] = createSelector(
  selectPhoneNumberSetsArray,
  (phoneNumberSets): PhoneNumberSet[] => {
    const primarySet = phoneNumberSets?.find(
      (phoneNumberSet: PhoneNumberSet) => phoneNumberSet.is_primary,
    );

    if (!primarySet) {
      return phoneNumberSets;
    }

    return [
      primarySet,
      ...phoneNumberSets.filter(
        (phoneNumberSet) =>
          phoneNumberSet.phone_numbers[0] !== primarySet.phone_numbers[0],
      ),
    ];
  },
);

export const selectDefaultPhoneNumberSet: (arg1: State) => PhoneNumberSet = (
  state,
) =>
  state.chat.phoneNumberSets[
    //@ts-expect-error
    state.chat.multiNumberInbox.selectedPhoneNumberSetId
  ] ||
  selectPrimaryPhoneNumberSet(state) ||
  first(selectPhoneNumberSetsArray(state));
export const selectDefaultPhoneNumberForPhoneInbox: (arg1: State) => string =
  createSelector(selectDefaultPhoneNumberSet, (phoneNumberSet) => {
    return phoneNumberSet?.phone_numbers[0];
  });

export const selectPhoneNumberSetFromDefaultPhoneNumberSet: (
  arg1: State,
) => string = createSelector(
  selectDefaultPhoneNumberSet,
  (phoneNumberSet): string => {
    const channel = phoneNumberSet?.['selected_channel'];
    const idKey = 'id';

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'PhoneNumberSet'.
    return phoneNumberSet?.[channel]?.[idKey];
  },
);
export const selectChannelFromDefaultPhoneNumberSet: (arg1: State) => string =
  createSelector(
    selectDefaultPhoneNumberSet,
    (phoneNumberSet): string => phoneNumberSet?.['selected_channel'],
  );

export const selectAutoReplyStatusFromPhoneNumberSet: (arg1: State) => string =
  createSelector(selectDefaultPhoneNumberSet, (phoneNumberSet): string =>
    phoneNumberSet?.autoreply?.active ? 'on' : 'off',
  );

export const selectPrimaryPhoneNumberSet: (
  arg1: State,
) => PhoneNumberSet | null | undefined = createSelector(
  selectPhoneNumberSetsArray,
  (phoneNumberSets): PhoneNumberSet | null | undefined =>
    phoneNumberSets?.find(
      (phoneNumberSet: PhoneNumberSet) => phoneNumberSet.is_primary,
    ),
);

export const selectFirstPhoneNumberSet = (
  state: State,
): PhoneNumberSet | null | undefined =>
  first(selectPhoneNumberSetsArray(state));

export const selectDisplayPhoneNumber = (
  state: State,
): string | null | undefined =>
  resolveDisplayPhoneNumber(selectDefaultPhoneNumberSet(state));

export const selectCurrentPhoneCountry: (arg1: State) => string =
  createSelector(
    selectDisplayPhoneNumber,
    selectDefaultPhoneCountry,
    (phoneNumber, defaultPhoneCountry) =>
      (phoneNumber && getCountryFromPhone(phoneNumber)) || defaultPhoneCountry,
  );

export const selectSMSInboxesAsOptionsForHVBroadcast = (
  state: State,
): GenericOptionsType[] => {
  const countryCode = selectCurrentPhoneCountry(state);
  const inbox = selectPhoneNumberSetsArray(state);
  // remove this condition when whatsapp is supported in HVB
  const smsInboxes = inbox.filter((inboxObj) => inboxObj.sms?.id);
  const inboxValuesToOptions = smsInboxes.map((smsInbox) => {
    const phoneNumber = smsInbox.phone_numbers[0];
    const formattedPhone = formatPhone(phoneNumber, countryCode) || phoneNumber;
    const label = smsInbox.name || formattedPhone;
    return {key: smsInbox.sms.id, label};
  });
  return inboxValuesToOptions;
};

export const selectWhatsappInboxesAsOptionsForHVBroadcast = (
  state: State,
): GenericOptionsType[] => {
  const countryCode = selectCurrentPhoneCountry(state);
  const inbox = selectPhoneNumberSetsArray(state);
  // remove this condition when whatsapp is supported in HVB
  const whatsappInboxes = inbox.filter((inboxObj) => inboxObj.whatsapp?.id);
  const inboxValuesToOptions = whatsappInboxes.map((whatsappInbox) => {
    const phoneNumber = whatsappInbox.phone_numbers[0];
    const formattedPhone = formatPhone(phoneNumber, countryCode) || phoneNumber;
    const label = whatsappInbox.name || formattedPhone;
    return {key: whatsappInbox.whatsapp.id, label};
  });
  return inboxValuesToOptions;
};

export const selectBroadcastRecipientLimitByPhoneNumberSetId = (
  state: State,
  phoneNumberSetId: string,
): number => {
  const inboxes = selectPhoneNumberSetsArray(state);
  const currentInbox = inboxes.find(
    (inboxObj) =>
      inboxObj.whatsapp?.id === phoneNumberSetId ||
      inboxObj.sms?.id === phoneNumberSetId,
  );
  return currentInbox?.broadcast_recipient_limit ?? 50000;
};

export const selectIsHighVolumeLine = (state: State): boolean => {
  const phoneNumberSet = selectDefaultPhoneNumberSet(state);
  return phoneNumberSet?.is_high_volume ?? false;
};
